import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Maximize2, FileText, Image as ImageIcon } from 'lucide-react';
import { ViewSiteVisitModal } from '../modals/ViewSiteVisitModal';
import { BASE_URL } from '@/lib/API';

export const SiteVisitAccordion = ({ lead }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!lead) {
    return null;
  }

  const siteVisitData = lead.siteVisitData || {};
  const hasData = (siteVisitData.measurements && siteVisitData.measurements.length > 0) || 
                  (siteVisitData.sitePhotos && siteVisitData.sitePhotos.length > 0);

  return (
    <div className="bg-blue-50/40 rounded-xl border border-blue-100/60 overflow-hidden">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-blue-50/60 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider flex items-center gap-2">
          <span className="w-1 h-3 bg-blue-500 rounded-full" />
          Site Visit Data {hasData ? `Available` : 'Not Available'}
        </div>
        <div className="flex items-center gap-3">
          {hasData && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              className="text-[10px] bg-white border border-blue-200 text-blue-600 px-2 py-1 rounded font-bold hover:bg-blue-50 flex items-center gap-1"
            >
              <Maximize2 size={12} /> View Full
            </button>
          )}
          {isOpen ? <ChevronUp size={16} className="text-blue-400" /> : <ChevronDown size={16} className="text-blue-400" />}
        </div>
      </div>
      
      {isOpen && (
        <div className="px-4 pb-4 border-t border-blue-100/50 pt-3">
          {hasData ? (
            <div className="flex flex-col gap-3">
              {siteVisitData.rooms?.map(room => {
                const roomMeasurements = siteVisitData.measurements?.filter(m => m.room === room) || [];
                if (roomMeasurements.length === 0) return null;
                
                return (
                  <div key={room} className="border border-slate-200 bg-white rounded p-3">
                    <h5 className="text-[11px] font-bold text-slate-600 mb-2 border-b pb-1">{room}</h5>
                    <div className="flex flex-col gap-2">
                      {roomMeasurements.map((m, idx) => (
                        <div key={idx} className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded flex flex-col gap-1">
                          {m.zone && <div><span className="font-semibold text-slate-700">Zone:</span> {m.zone}</div>}
                          <div>
                            <span className="font-semibold text-slate-700">Dims:</span>{' '}
                            {[m.width ? `W: ${m.width}` : '', m.height ? `H: ${m.height}` : '', m.depth ? `D: ${m.depth}` : ''].filter(Boolean).join(' x ') || 'N/A'}
                          </div>
                          {m.particulars && <div className="truncate"><span className="font-semibold text-slate-700">Details:</span> {m.particulars}</div>}
                          
                          {m.photos && m.photos.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-200/60">
                              <span className="text-[9px] font-bold text-slate-400 uppercase block mb-1">Attached Photos ({m.photos.length})</span>
                              <div className="flex flex-wrap gap-2">
                                {m.photos.map(p => p.fileId && (
                                  <a key={p.fileId} href={`${BASE_URL}/api/verve-leads/site-visit/media/${p.fileId}`} target="_blank" rel="noopener noreferrer" className="block w-12 h-12 rounded border border-slate-200 overflow-hidden shrink-0 hover:border-blue-400 transition-colors relative group bg-slate-100">
                                    {p.mimetype?.startsWith('video') ? (
                                      <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-400" /></div>
                                    ) : (
                                      <img src={`${BASE_URL}/api/verve-leads/site-visit/media/${p.fileId}`} alt="site visit" className="w-full h-full object-cover" />
                                    )}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                      <Maximize2 className="w-4 h-4 text-white" />
                                    </div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {siteVisitData.sitePhotos && siteVisitData.sitePhotos.length > 0 && (
                <div className="mt-2 border border-slate-200 bg-white rounded p-3">
                  <h5 className="text-[11px] font-bold text-slate-600 mb-2 border-b pb-1">Site Photos</h5>
                  <div className="flex flex-wrap gap-2">
                    {siteVisitData.sitePhotos.map(p => p.fileId && (
                      <a key={p.fileId} href={`${BASE_URL}/api/verve-leads/site-visit/media/${p.fileId}`} target="_blank" rel="noopener noreferrer" className="block w-12 h-12 rounded border border-slate-200 overflow-hidden shrink-0 hover:border-blue-400 transition-colors relative group bg-slate-100">
                        {p.mimetype?.startsWith('video') ? (
                          <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-400" /></div>
                        ) : (
                          <img src={`${BASE_URL}/api/verve-leads/site-visit/media/${p.fileId}`} alt="site visit photo" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                          <Maximize2 className="w-4 h-4 text-white" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {siteVisitData.pdfDocuments && siteVisitData.pdfDocuments.length > 0 && (
                <div className="mt-1 border border-slate-200 bg-white rounded p-3">
                  <h5 className="text-[11px] font-bold text-slate-600 mb-2 border-b pb-1">Attached Documents</h5>
                  <div className="flex flex-col gap-1.5">
                    {siteVisitData.pdfDocuments.map(pdf => (
                      <button 
                        key={pdf.driveFileId || pdf._id} 
                        onClick={(e) => {
                          e.stopPropagation();
                          import('@/lib/API').then(({ downloadFile }) => {
                            downloadFile(`${BASE_URL}${pdf.fileUrl}`, pdf.filename);
                          });
                        }}
                        className="flex items-center gap-2 text-[11px] text-blue-600 hover:underline hover:bg-slate-100 bg-slate-50 p-2 rounded border border-slate-100 transition-colors w-full text-left"
                      >
                        <FileText size={14} className="text-red-500" />
                        <span className="truncate">{pdf.filename}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 italic text-center py-2">
              No site visit measurements have been recorded yet.
            </div>
          )}
        </div>
      )}

      {/* Full Modal View */}
      {isModalOpen && (
        <ViewSiteVisitModal 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lead={lead}
        />
      )}
    </div>
  );
};
